<template>
    <div v-show="isAddQna == false" class="main_div" :key="componentKey">
        <div style="height: auto">
            <n-space>
                <NCard style="margin-top: 20px; margin-left: 80px; border: 0px; min-width: 800px">
                    <n-space item-style="display: flex;" align="center">
                        <div style="font-size: 20px; width: 150px; font-weight: bold">FAQ 관리</div>
                    </n-space>
                    <div class="content_delimiter_line"></div>
                    <Loading v-show="loading" />
                    <div>
                        <button @click="showAddQna" class="action-button">FAQ 등록</button>
                        <div v-if="listLoading" class="loading-indicator">FAQ 목록을 불러오는 중...</div>
                        <div v-if="error" class="error-message">{{ error }}</div>
                        <div v-if="!listLoading && !error">
                            <div class="table-container">
                                <n-data-table
                                    v-if="qnaList.length > 0"
                                    :columns="columns"
                                    :data="qnaList"
                                    :pagination="false"
                                    :bordered="true"
                                />
                                <p v-else>등록된 FAQ가 없습니다.</p>
                            </div>
                            <div
                                v-if="qnaList.length > 0"
                                class="pagination-controls"
                                style="margin-top: 15px; text-align: center"
                            >
                                <n-button
                                    @click="wrappedGoToPreviousPage"
                                    :disabled="currentPage === 1"
                                    style="margin-right: 8px"
                                >
                                    이전 페이지
                                </n-button>
                                <span style="margin: 0 10px">{{ currentPage }} 페이지</span>
                                <n-button @click="wrappedGoToNextPage" :disabled="!hasNextPage"> 다음 페이지 </n-button>
                            </div>
                        </div>
                    </div>
                </NCard>
                <NModal
                    v-model:show="isAlert"
                    :mask-closable="false"
                    :content="alertMessage"
                    :type="alertType"
                    preset="dialog"
                    title="Dialog"
                    positive-text="확인"
                    @positive-click="onPositiveClick"
                >
                </NModal>
                <NModal v-model:show="isShowContent" @after-leave="onContentModalClose">
                    <n-card
                        style="width: 800px; max-height: 80vh; overflow-y: auto"
                        title="답변"
                        :bordered="false"
                        size="huge"
                        role="dialog"
                        aria-modal="true"
                    >
                        <div
                            v-if="isContentLoading"
                            class="loading-indicator"
                            style="text-align: center; padding: 20px"
                        >
                            내용을 불러오는 중...
                        </div>
                        <div v-else-if="contentError" class="error-message" style="color: red; padding: 20px">
                            {{ contentError }}
                        </div>
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <div v-else v-html="contentMessage"></div>
                        <template #footer>
                            <n-button @click="isShowContent = false">닫기</n-button>
                        </template>
                    </n-card>
                </NModal>
            </n-space>
        </div>
    </div>
    <AddQna v-show="isAddQna == true" @hide="hideAddQna" :isAddQna="isAddQna" :selectedItem="selectedItem"> </AddQna>
</template>

<script setup lang="ts">
import { ref, onMounted, h } from "vue";
import { getCallable, makeCallApiParam } from "@/config/env";
import {
    NDataTable,
    NSpace,
    NCheckbox,
    NCard,
    NSelect,
    NButton,
    NIcon,
    NModal,
    NRadio,
    NRadioGroup,
    NInput,
    FormInst,
    FormRules,
    useMessage,
    useNotification,
    useDialog,
} from "naive-ui";
import Loading from "@/components/Loading.vue";
import * as common from "@/assets/common/common";
import * as constant from "@/constants/constant";
import AddQna from "@/views/qna/AddQna.vue";
import { Qna } from "@/model/qna";
import { QueryInfo } from "@/model/common";
import { usePagination } from "@/composables/usePagination";
import { useAuth } from "@/composables/useAuth";
import { useActionDialog } from "@/composables/useDialog";
const { show: showActionDialog } = useActionDialog();
const { loading, isLoggedIn } = useAuth();

const message = useMessage();
const notification = useNotification();
const dialog = useDialog();

const getCallApiCallable = getCallable("callApi");
const getBoards = "getBoards";
const deleteQnaCallable = getCallable("deleteBoard");
const componentKey = ref(0);
const deleteConfirmMessage = ref<string | null>(null);
const contentMessage = ref<string | null>(null);
const alertMessage = ref<string | null>(null);
const alertType = ref<string | null>(null);
const isDeleteConfirm = ref<boolean>(false);
const deleteConfirmType = ref<string | null>(null);
const isAddQna = ref<boolean>(false);
const isShowContent = ref<boolean>(false);
const isAlert = ref<boolean>(false);
const isDeleting = ref<boolean>(false);
const isContentLoading = ref<boolean>(false);
const contentError = ref<string | null>(null);
const selectedItem = ref<Qna | null>(null);

const {
    paginatedItems: qnaList,
    listLoading,
    error,
    currentPage,
    hasNextPage,
    goToNextPage: composableGoToNextPage,
    goToPreviousPage: composableGoToPreviousPage,
    refreshData: composableRefreshQnas,
} = usePagination<Qna>(
    async (params) =>
        getCallApiCallable(
            makeCallApiParam(getBoards, {
                ...params,
                type: constant.boardType.QNA,
            })
        ),
    25 // 페이지 크기
);

const registrationRules: FormRules = {
    title: [
        {
            required: true,
            message: "제목을 입력해주세요.",
            trigger: ["input", "blur"],
        },
    ],
    content: [
        {
            required: true,
            message: "내용을 입력해주세요.",
            trigger: ["input", "blur"],
        },
    ],
};

async function wrappedRefreshQnas() {
    loading.value = true; // 전역 로딩 시작
    await composableRefreshQnas();
    loading.value = false; // 전역 로딩 종료
}

const columns = [
    {
        title: "순서",
        key: "SK",
        resizable: true,
        render(row: Qna) {
            const orderNum = row.SK.split("#")[1];
            return Number(orderNum);
        },
    },
    {
        title: "질문",
        key: "question",
        resizable: true,
    },
    {
        title: "답변",
        sorter: "answer",
        render(row: Qna) {
            return h(
                NButton,
                {
                    size: "small",
                    onClick: () => {
                        onShowContent(row);
                    },
                    bordered: true,
                },
                { default: () => "내용 보기" }
            );
        },
    },
    {
        title: "작성자",
        key: "author",
        resizable: true,
    },
    {
        title: "작성일",
        key: "createdDate",
        render(row: Qna) {
            return common.getDate(row.createdDate);
        },
    },
    {
        title: "삭제",
        sorter: "default",
        render(row: Qna) {
            return h(
                NButton,
                {
                    size: "small",
                    onClick: () => {
                        deleteQnaItem(row);
                    },
                    bordered: true,
                },
                { default: () => "삭제" }
            );
        },
    },
];

function showAddQna() {
    isAddQna.value = true;
}

async function hideAddQna() {
    selectedItem.value = null;
    isAddQna.value = false;
    await wrappedRefreshQnas();
}
async function onShowContent(row: Qna) {
    isShowContent.value = true;
    contentMessage.value = row.answer;
}

function onPositiveClick() {
    isAlert.value = false;
}

async function wrappedGoToNextPage() {
    if (hasNextPage.value) {
        await composableGoToNextPage();
    }
}

async function wrappedGoToPreviousPage() {
    if (currentPage.value > 1) {
        await composableGoToPreviousPage();
    }
}

function deleteQnaItem(qna: Qna) {
    showActionDialog({
        title: "확인",
        content: "FAQ을 하시겠습니까?",
        positiveText: "삭제",
        negativeText: "아니요",
        positiveButtonProps: { type: "error", loading: isDeleting.value },
        confirmButtonLoadingText: "삭제 중...",
        onConfirm: async () => {
            try {
                const result = await deleteQnaCallable({
                    boardType: constant.boardType.QNA,
                    SK: qna.SK,
                });
                if (common.evalFunctionResult(result)) {
                    message.success("FAQ 삭제가 완료되었습니다.");
                    await wrappedRefreshQnas();
                } else {
                    throw new Error(common.getResultError(result) || "FAQ 삭제 중 오류가 발생했습니다.");
                }
            } catch (error: any) {
                console.error("FAQ 삭제 API 호출 실패:", error);
                message.error(common.getResultError(error) || "FAQ 삭제 중 오류가 발생했습니다.");
            }
        },
    });
}
function onContentModalClose() {
    contentMessage.value = "";
    contentError.value = null;
    isContentLoading.value = false;
}

onMounted(() => {
    wrappedRefreshQnas();
});
</script>

<style scoped></style>
